import { List, Map } from "immutable";
import { applyState, isNew } from "./lib/state";
import uuid from "./lib/uuid";
function addKeyToTemplate(el, key) {
    const keyedTemplate = el;
    keyedTemplate.key = key;
    return keyedTemplate;
}
let _modalState = Map({
    modalStack: List(),
    modalNodes: List(),
    closeCallbacks: Map(),
});
/**
 * Private removal function via index.
 * Looks for (and executes) an associated callback function for the modal at the given index.
 * Deletes both the template and the cached DOM node at the index.
 */
function removeModal(index) {
    var _a;
    if (index >= 0) {
        // Safeguard against negative indices, which are supported by the immutable List.
        const key = (_a = this.modalStack.get(index)) === null || _a === void 0 ? void 0 : _a.key;
        const callback = this.closeCallbacks.get(key);
        if (callback !== undefined) {
            callback();
        }
        this.modalState = applyState(this.modalState, {
            modalStack: this.modalStack.delete(index),
            modalNodes: this.modalNodes.delete(index),
            closeCallbacks: this.closeCallbacks.delete(key),
        });
    }
}
/** Replaces a modal with the given key, updating both the template and the closeCallback. */
function replaceModal(key, template, closeCallback) {
    const index = this.modalStack.findIndex((kt) => kt.key === key);
    if (index >= 0) {
        this.modalState = applyState(this.modalState, {
            modalStack: this.modalStack.set(index, addKeyToTemplate(template, key)),
            closeCallbacks: closeCallback !== undefined
                ? this.closeCallbacks.set(key, closeCallback)
                : this.closeCallbacks.delete(key),
        });
    }
}
/**
 * A singleton Lit controller that manages the state of [[ModalPortal]].
 */
const modalController = {
    host: undefined,
    set modalState(newState) {
        if (isNew(newState, "modalStack", this.modalStack)) {
            this.host.offerState(Map({
                modalStack: newState.get("modalStack"),
            }));
        }
        _modalState = newState;
    },
    get modalState() {
        return _modalState;
    },
    get modalStack() {
        return this.modalState.get("modalStack");
    },
    get modalNodes() {
        return this.modalState.get("modalNodes");
    },
    get closeCallbacks() {
        return this.modalState.get("closeCallbacks");
    },
    attach(host) {
        if (this.host === undefined) {
            (this.host = host).addController(this);
        }
        else {
            console.error("You attempted to attach a singleton controller to more than one host.");
        }
    },
    hostConnected() {
        this.host.offerState(Map({ modalStack: this.modalStack }));
    },
    hostUpdated() {
        // We need to maintain a concurrent list of the modals to support asynchronous and out-of-order modal popping.
        this.modalState = applyState(this.modalState, {
            modalNodes: List(this.host.modalNodes),
        });
    },
    push(template, closeCallback) {
        const key = uuid();
        this.modalState = applyState(this.modalState, {
            modalStack: this.modalStack.push(addKeyToTemplate(template, key)),
            closeCallbacks: closeCallback !== undefined
                ? this.closeCallbacks.set(key, closeCallback)
                : this.closeCallbacks,
        });
        return {
            remove: () => this.removeByKey(key),
            replace: (template, closeCallback) => replaceModal.call(this, key, template, closeCallback),
        };
    },
    pop() {
        removeModal.call(this, this.modalStack.size - 1);
    },
    removeByNode(modal) {
        removeModal.call(this, this.modalNodes.indexOf(modal));
    },
    removeByKey(key) {
        removeModal.call(this, this.modalStack.findIndex((kt) => kt.key === key));
    },
    removeAll() {
        while (this.modalStack.size > 0) {
            this.pop();
        }
    },
    // in modalController implementation
    disconnect(host) {
        if (this.host === host) {
            this.host = undefined;
            host.removeController(this);
        }
    },
};
export default modalController;
//# sourceMappingURL=lxl-modal-controller.js.map